import { useId, useRef, useState, type ReactNode } from "react";
import { ResourceRegistrationModal } from "@/components/resources/ResourceRegistrationModal";
import { CONTACT_FORM_ENDPOINT } from "@/config/constants";
import type { ResourceItem } from "@/data/content";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { trackEvent } from "@/utils/analytics";
import {
  getResourcesRegistration,
  setResourcesRegistration,
  type ResourcesRegistration,
} from "@/utils/resourcesAccess";

function openResource(resource: ResourceItem): void {
  trackEvent("resource_download", { resource: resource.title });
  if (resource.link) {
    window.open(resource.link, "_blank", "noopener,noreferrer");
  }
}

async function notifyResourceLead(data: ResourcesRegistration, message: string) {
  const res = await fetch(CONTACT_FORM_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: data.name, email: data.email, message, source: "resources" }),
  });
  if (!res.ok) throw new Error(await res.text());
}

/**
 * Download flow for free resources: registered visitors download straight
 * away; first-timers leave name and email in a dialog first, which also
 * notifies Jenifer of the new lead.
 */
export function useResourceAccess(): { requestAccess: (item: ResourceItem) => void; modal: ReactNode } {
  const modalTitleId = useId();
  const pendingRef = useRef<ResourceItem | null>(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<string | undefined>();
  const { t } = useAppTranslation("resourcesPage");

  function requestAccess(item: ResourceItem) {
    if (getResourcesRegistration() !== null) {
      openResource(item);
      return;
    }
    pendingRef.current = item;
    setTitle(item.title);
    setOpen(true);
  }

  function dismiss() {
    pendingRef.current = null;
    setTitle(undefined);
    setOpen(false);
  }

  async function registered(data: ResourcesRegistration) {
    const pending = pendingRef.current;
    dismiss();

    const previous = getResourcesRegistration();
    const isNewLead = !previous || previous.email !== data.email || previous.name !== data.name;
    setResourcesRegistration(data);
    if (pending) openResource(pending);

    if (isNewLead) {
      trackEvent("resource_register", { resource: pending?.title });
      const message = pending?.title ? t("leadMessageWithTitle", { title: pending.title }) : t("leadMessage");
      try {
        await notifyResourceLead(data, message);
      } catch {
        /* The lead email is optional; the download already started. */
      }
    }
  }

  const modal = open ? (
    <ResourceRegistrationModal
      titleId={modalTitleId}
      resourceTitle={title}
      defaultValues={getResourcesRegistration() ?? undefined}
      onRegistered={registered}
      onDismiss={dismiss}
    />
  ) : null;

  return { requestAccess, modal };
}
